/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Heart,
  Bed,
  Bath,
  Car,
  Maximize,
  Compass,
  Layers,
  Clock,
  Check,
  Phone as PhoneIcon,
  MessageCircle,
  Mail,
  Share2,
  Pencil,
  Pause as PauseIcon,
  Play,
  X,
} from "lucide-react";
import { propertiesApi, photoUrl } from "./api";
import { formatInr, pricePerSqft } from "../utils/inrFormat";
import { resolveAvatarUrl } from "../utils/avatarUrl";
import ExternalContactForm from "./ExternalContactForm";
import ExternalViewersAudit from "./ExternalViewersAudit";
import useModalA11y from "../utils/useModalA11y";
import "./PropertyDetail.css";

function isLoggedIn() {
  try {
    return !!JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return false;
  }
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInquiry, setShowInquiry] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await propertiesApi.get(id);
      setListing(d);
      setActiveImage(0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSave = async () => {
    if (!listing) return;
    try {
      if (listing.is_saved) {
        await propertiesApi.unsave(listing.id);
      } else {
        await propertiesApi.save(listing.id);
      }
      load();
    } catch (_) {}
  };

  const pause = async () => {
    try {
      await propertiesApi.pause(listing.id);
      load();
    } catch (e) {
      alert(e.message);
    }
  };
  const resume = async () => {
    try {
      await propertiesApi.resume(listing.id);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) return <div className="vsp-d-shell vsp-d-empty">Loading…</div>;
  if (error)
    return (
      <div className="vsp-d-shell">
        <div className="vsp-d-error">{error}</div>
      </div>
    );
  if (!listing) return null;

  const photos = listing.photos || [];
  const primary = photos[activeImage] || photos[0];
  const isRent = listing.type === "RENT";
  const price = isRent
    ? formatInr(listing.monthly_rent_inr, { monthly: true })
    : formatInr(listing.expected_price_inr);
  const persqft = !isRent
    ? pricePerSqft(listing.expected_price_inr, listing.built_up_area_sqft)
    : null;

  const stats = [
    { Icon: Bed, label: "Bedrooms", value: listing.bedrooms },
    { Icon: Bath, label: "Bathrooms", value: listing.bathrooms },
    { Icon: Car, label: "Parking", value: listing.parking },
    {
      Icon: Maximize,
      label: "Area",
      value: `${listing.built_up_area_sqft.toLocaleString("en-IN")} sqft`,
    },
    listing.facing && {
      Icon: Compass,
      label: "Facing",
      value: listing.facing.charAt(0) + listing.facing.slice(1).toLowerCase(),
    },
    listing.property_age_years != null && {
      Icon: Clock,
      label: "Age",
      value: `${listing.property_age_years} years`,
    },
    {
      Icon: Check,
      label: "Furnishing",
      value:
        listing.furnishing === "FURNISHED"
          ? "Fully"
          : listing.furnishing === "SEMI"
          ? "Semi"
          : "Unfurnished",
    },
  ].filter(Boolean);

  const amenities = listing.amenities || {};

  return (
    <div className="vsp-d-shell">
      <div className="vsp-d-topbar">
        <Link to="/properties" className="vsp-d-back">
          <ArrowLeft size={14} /> All properties
        </Link>
        <div className="vsp-d-top-actions">
          <button onClick={toggleSave} className="vsp-d-icon-btn" title="Save">
            <Heart
              size={14}
              fill={listing.is_saved ? "#dc2626" : "none"}
              color={listing.is_saved ? "#dc2626" : "currentColor"}
            />
          </button>
          {listing.can_edit && (
            <>
              <button
                onClick={() => navigate(`/properties/${listing.id}/edit`)}
                className="vsp-d-icon-btn"
                title="Edit"
              >
                <Pencil size={14} />
              </button>
              {listing.status === "ACTIVE" && (
                <button
                  onClick={pause}
                  className="vsp-d-icon-btn"
                  title="Pause"
                >
                  <PauseIcon size={14} />
                </button>
              )}
              {listing.status === "PAUSED" && (
                <button
                  onClick={resume}
                  className="vsp-d-icon-btn"
                  title="Resume"
                >
                  <Play size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="vsp-d-title-row">
        <div>
          <h1>
            {listing.villa?.villa_number ? (
              <Link
                to={`/villas/${encodeURIComponent(listing.villa.villa_number)}`}
                className="vsp-d-villa-link"
              >
                {listing.villa.villa_number}
              </Link>
            ) : (
              "Villa"
            )}{" "}— Vishal Sanjivini
          </h1>
          <div className="vsp-d-pills">
            <span
              className={`vsp-d-type ${
                isRent ? "rent" : "sale"
              }`}
            >
              {isRent ? "For Rent" : "For Sale"}
            </span>
            <span
              className={`vsp-d-status status-${listing.status.toLowerCase()}`}
            >
              {listing.status}
            </span>
          </div>
        </div>
      </div>

      <div className="vsp-d-grid">
        <div className="vsp-d-main">
          <div className="vsp-d-gallery">
            {primary ? (
              <img src={photoUrl(primary.url)} alt="" className="vsp-d-hero" />
            ) : (
              <div className="vsp-d-hero-placeholder">No photos</div>
            )}
            {photos.length > 1 && (
              <div className="vsp-d-thumbs">
                {photos.map((p, idx) => (
                  <button
                    key={p.id}
                    className={`vsp-d-thumb ${
                      idx === activeImage ? "active" : ""
                    }`}
                    onClick={() => setActiveImage(idx)}
                  >
                    <img src={photoUrl(p.url)} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="vsp-d-card">
            <div className="vsp-d-price-row">
              <div className="vsp-d-price">{price}</div>
              {isRent && listing.monthly_maintenance_inr > 0 && (
                <div className="vsp-d-maint">
                  + {formatInr(listing.monthly_maintenance_inr, { monthly: true })}{" "}
                  maintenance
                </div>
              )}
              {!isRent && persqft && (
                <div className="vsp-d-maint">{persqft}</div>
              )}
              {!isRent && listing.price_negotiable && (
                <span className="vsp-d-neg-pill">Negotiable</span>
              )}
            </div>

            <div className="vsp-d-stats">
              {stats.map((s) => (
                <div key={s.label} className="vsp-d-stat">
                  <div className="vsp-d-stat-h">
                    <s.Icon size={13} /> {s.label}
                  </div>
                  <div className="vsp-d-stat-v">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="vsp-d-terms">
              {isRent && listing.available_from && (
                <Row label="Available from" value={listing.available_from} />
              )}
              {isRent && listing.min_lease_months && (
                <Row
                  label="Minimum lease"
                  value={`${listing.min_lease_months} months`}
                />
              )}
              {isRent && listing.security_deposit_months && (
                <Row
                  label="Security deposit"
                  value={`${listing.security_deposit_months} months`}
                />
              )}
              {!isRent && listing.possession && (
                <Row
                  label="Possession"
                  value={
                    listing.possession === "READY"
                      ? "Ready to move"
                      : listing.possession.replace(/_/g, " ").toLowerCase()
                  }
                />
              )}
              <Row label="Visibility" value={listing.visibility} />
              {listing.expires_at && (
                <Row
                  label="Expires"
                  value={new Date(listing.expires_at).toLocaleDateString()}
                />
              )}
            </div>
          </div>

          <div className="vsp-d-card">
            <h2>Description</h2>
            <p className="vsp-d-desc">{listing.description}</p>
          </div>

          {(listing.vegetarian_only ||
            listing.pets_allowed ||
            listing.non_smokers_only) && (
            <div className="vsp-d-card">
              <h2>Preferences</h2>
              <div className="vsp-d-prefs">
                {listing.vegetarian_only && (
                  <span className="vsp-d-pref">Vegetarians only</span>
                )}
                {listing.pets_allowed && (
                  <span className="vsp-d-pref">Pets allowed</span>
                )}
                {listing.non_smokers_only && (
                  <span className="vsp-d-pref">Non-smokers only</span>
                )}
              </div>
            </div>
          )}

          {Object.keys(amenities).length > 0 && (
            <div className="vsp-d-card">
              <h2>Amenities</h2>
              {Object.entries(amenities).map(([cat, items]) => (
                <div key={cat} className="vsp-d-amen-cat">
                  <h4>{cat.charAt(0).toUpperCase() + cat.slice(1)}</h4>
                  <div className="vsp-d-amen-items">
                    {items.map((a) => (
                      <span key={a} className="vsp-d-amen">
                        <Check size={12} /> {a}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="vsp-d-side" id="vsp-d-contact-anchor">
          <div className="vsp-d-card vsp-d-contact">
            <div className="vsp-d-owner">
              <div className="vsp-d-owner-avatar">
                {listing.owner?.avatar_url ? (
                  <img
                    src={resolveAvatarUrl(listing.owner.avatar_url)}
                    alt=""
                  />
                ) : (
                  <span>{(listing.owner?.name || "?")[0]}</span>
                )}
              </div>
              <div>
                <div className="vsp-d-owner-name">{listing.owner?.name}</div>
                <div className="vsp-d-owner-meta">
                  Owner · {listing.villa?.villa_number}
                </div>
              </div>
            </div>

            {!listing.can_edit && listing.status === "ACTIVE" && (
              <>
                {!listing.owner?.phone_requires_otp && (
                  <button
                    className="vsp-d-btn-primary"
                    onClick={() => setShowInquiry(true)}
                  >
                    Send Inquiry
                  </button>
                )}
                {listing.show_phone && listing.owner?.phone && (
                  <a
                    href={`tel:+91${listing.owner.phone}`}
                    className="vsp-d-btn-secondary vsp-d-btn-icon"
                    aria-label="Call owner"
                    title="Call owner"
                  >
                    <PhoneIcon size={17} />
                  </a>
                )}
                {listing.owner?.phone && (
                  <a
                    href={`https://wa.me/91${listing.owner?.phone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="vsp-d-btn-secondary vsp-d-btn-icon"
                    aria-label="WhatsApp owner"
                    title="WhatsApp owner"
                  >
                    <MessageCircle size={17} />
                  </a>
                )}
              </>
            )}
            {listing.can_edit && (
              <p className="vsp-d-self-note">
                This is your own listing — you can pause it or edit
                details from the header.
              </p>
            )}
            {listing.status !== "ACTIVE" && !listing.can_edit && (
              <p className="vsp-d-self-note">
                This listing isn't currently accepting inquiries.
              </p>
            )}
          </div>

          {/* External viewer flow — only when the response says phone
              requires OTP and the listing is active. */}
          {!listing.can_edit &&
            listing.status === "ACTIVE" &&
            listing.owner?.phone_requires_otp && (
              <ExternalContactForm listing={listing} />
            )}

          {/* Owner-side audit log */}
          {listing.can_edit && (
            <ExternalViewersAudit listingId={listing.id} />
          )}
        </aside>
      </div>

      {showInquiry && (
        <InquiryModal
          listing={listing}
          onClose={() => setShowInquiry(false)}
          onSubmitted={() => {
            setShowInquiry(false);
            alert("Inquiry sent — the owner has been notified.");
          }}
        />
      )}

      {/* Mobile-only sticky CTA. On phones the contact aside renders
          below 6+ cards of details, so we surface the primary action at
          the bottom of the viewport. Hidden on desktop where the aside
          is already visible. */}
      {!listing.can_edit && listing.status === "ACTIVE" && (
        <div className="vsp-d-mobile-cta">
          {listing.owner?.phone_requires_otp ? (
            <button
              className="vsp-d-btn-primary vsp-d-mobile-cta-btn"
              onClick={() => {
                const node = document.getElementById("vsp-d-contact-anchor");
                node?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Get owner contact
            </button>
          ) : (
            <button
              className="vsp-d-btn-primary vsp-d-mobile-cta-btn"
              onClick={() => setShowInquiry(true)}
            >
              Send Inquiry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="vsp-d-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function InquiryModal({ listing, onClose, onSubmitted }) {
  const modalRef = useModalA11y({ isOpen: true, onClose });
  const me = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const [message, setMessage] = useState(
    `I'm interested in this ${listing.type === "RENT" ? "rental" : "property"} — could we schedule a visit?`
  );
  const [visit, setVisit] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await propertiesApi.submitInquiry(listing.id, {
        message,
        preferred_visit: visit || null,
      });
      onSubmitted && onSubmitted();
    } catch (err) {
      setError(err.message || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vsp-d-overlay" onClick={onClose}>
      <form
        ref={modalRef}
        className="vsp-d-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vsp-d-inq-title"
      >
        <div className="vsp-d-modal-head">
          <h2 id="vsp-d-inq-title">Send inquiry</h2>
          <button
            type="button"
            className="vsp-d-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="vsp-d-modal-row">
          <strong>From:</strong> {me.name} · +91 {me.phone_number}
        </div>
        <div className="vsp-d-modal-field">
          <label>Message</label>
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hi, I'm interested…"
          />
        </div>
        <div className="vsp-d-modal-field">
          <label>Preferred visit time</label>
          <select value={visit} onChange={(e) => setVisit(e.target.value)}>
            <option value="">— optional —</option>
            <option value="morning">Weekday morning</option>
            <option value="afternoon">Weekday afternoon</option>
            <option value="evening">Weekday evening</option>
            <option value="weekend">Weekend</option>
          </select>
        </div>
        {error && <div className="vsp-d-modal-error">{error}</div>}
        <div className="vsp-d-modal-actions">
          <button
            type="button"
            className="vsp-d-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vsp-d-btn-primary"
            disabled={busy}
          >
            {busy ? "Sending…" : "Send inquiry"}
          </button>
        </div>
      </form>
    </div>
  );
}
